<script setup>
/* eslint-disable */
import { onMounted, computed, ref, watch } from 'vue';
import { useBannersStore } from '@/stores/banners';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import { useMenuStore } from '@/stores/menus';
import { useCustomerStore } from '@/stores/customer';
import { useRacingSportsStore } from '@/stores/racing_sports';
import { openModal } from '@kolirt/vue-modal';
import Login from "@/components/modals/Login.vue";
import VerificationEmail from "@/components/modals/VerificationEmail.vue";
import { useRouter } from "vue-router"
import SportsbookMissions from '@/components/modals/missions/SportsbookMissions.vue';
import { useGeneralStore } from '@/stores/general';

const router  = useRouter();

// Define Stores
const bannersStore = useBannersStore();
const menuStore = useMenuStore();
const customerStore = useCustomerStore();
const racingSportsStore = useRacingSportsStore();
const generalStore = useGeneralStore();

// Define Variables
const current_banner = ref(0);
const scroll_interval = 4000;
const is_rtl = ref(false);
const heroSplideRef = ref(null);

// Get active hero banner
const activeHeroBanner = computed(() => {
	if (bannersStore.homepage_banners.length == 0) return null;

	return bannersStore.homepage_banners.find(banner => banner.id === current_banner.value);
});

const hasBanners = computed(() => bannersStore.homepage_banners.length > 0);

const heroSliderOptions = computed(() => ({
	perPage: 1,
	drag: true,
	arrows: false,
	pagination: false,
	autoplay: true,
	interval: 10000,
	type: 'loop',
	direction: is_rtl.value ? 'rtl' : 'ltr',
}));

const getBannerIndexById = (banner_id) =>
	bannersStore.homepage_banners.findIndex((banner) => banner.id === banner_id);

// Select a banner
const selectBanner = (banner_id) => {
	current_banner.value = banner_id;
	const bannerIndex = getBannerIndexById(banner_id);
	if (bannerIndex < 0) return;
	heroSplideRef.value?.splide?.go(bannerIndex);
};

// Go to next banner
const goToNextHeroBanner = () => {
	// Find the current active banner's index
	const current_index = bannersStore.homepage_banners.findIndex(b => b.id === current_banner.value);

	// Find the next banner's index, if it's the last banner, go to the first one
	const next_index = (current_index + 1) % bannersStore.homepage_banners.length;

	// Update the current banner to the next one
	current_banner.value = bannersStore.homepage_banners[next_index].id;
};

const handleMenuBoxRedirect = (path) => {
	if (!path) return;
	if (path == '/missions') {
		openModal(SportsbookMissions, {mission_type: 'all'}).catch(() => {});
		return;
	}

	// This was made at 20:45 2 days before ICE 2025 :)
	if (path == '/racing') {
		let first_sport = racingSportsStore.racingSports[0];

		router.push({name: 'racing-coupons', params: {sport_id: first_sport.id}});
		racingSportsStore.setSelectedRacingSportId(first_sport.id);
		return;
	}

	let login_required = (path.includes('/account') || path.includes('/casino/game/') || path.includes('/live-casino/table/'));
	let verification_required = (path.includes('/account') || path.includes('/casino/game/') || path.includes('/live-casino/table/'));

	if (login_required && !customerStore.isLoggedIn) openModal(Login).catch(() => {});
	else if (verification_required && !customerStore.hasVerifiedEmail) openModal(VerificationEmail).catch(() => {});
	else router.push(path);
}

const goToPage = ((url) => {
	if (url.startsWith(process.env.VUE_APP_SITE_URL)) {
		const sliced = url.slice(process.env.VUE_APP_SITE_URL.length);

		router.push({'path': sliced});
		return;
	}

	window.location.href = url;
});

const onSlideClick = (slide) => {
	if (!bannersStore.is_button_visible(slide.id) && slide.button_url) goToPage(slide.button_url);
}

const onHeroMoved = (_splide, newIndex) => {
	const banner = bannersStore.homepage_banners[newIndex];
	if (!banner) return;
	current_banner.value = banner.id;
};

const onHeroMounted = (splide) => {
	const banner = bannersStore.homepage_banners[splide.index];
	if (banner) current_banner.value = banner.id;
};

onMounted(() => {
	is_rtl.value = document.documentElement.getAttribute('dir') === 'rtl';
	if (bannersStore.homepage_banners.length > 0) current_banner.value = bannersStore.homepage_banners[0].id;
});

watch(() => bannersStore.homepage_banners.length, (newLength, oldLength) => {
	if (newLength > oldLength) current_banner.value = bannersStore.homepage_banners[0].id;
});
</script>
<template>
	<section v-if="hasBanners" class="home-hero">
		<!-- banner main -->
		<Splide
			ref="heroSplideRef"
			:options="heroSliderOptions"
			@splide:moved="onHeroMoved"
			@splide:mounted="onHeroMounted"
		>
			<SplideSlide v-for="banner in bannersStore.homepage_banners" :key="banner.id">
				<div class="slide h-100">
					<!-- banner image -->
					<div class="slide-img" :class="{ 'is-clickable': !bannersStore.is_button_visible(banner.id) && banner.button_url }">
						<img :src="banner.media_url" alt="Image 1" @click="onSlideClick(banner)">
					</div>
					<!-- end -->

					<!-- content -->
					<!-- <div class="banner-content">
						<div class="title">
							<h1>{{ banner.title }}</h1>
						</div>
						<div class="banner-content-area">
							<div class="sub-title">
								<h5 class="mw-60 fw-400">{{ banner.sub_title }}</h5>
							</div>
							<div v-if="bannersStore.is_button_visible(banner.id)" class="banner-btn">
								<a v-if="banner.button_url"
									@click="goToPage(banner.button_url)" class="view-all-btn" :class="{ 'btn-gradient': generalStore.getGeneral.use_gradient_color_as_button }">{{ banner.button_text }}
									&nbsp;
									<svg id="Arrow" xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8">
										<rect id="Rectangle_1315" data-name="Rectangle 1315" width="8" height="1" transform="translate(0 7)" fill="var(--button-color-text)"/>
										<rect id="Rectangle_1316" data-name="Rectangle 1316" width="8" height="1" transform="translate(8) rotate(90)" fill="var(--button-color-text)"/>
										<rect id="Rectangle_1317" data-name="Rectangle 1317" width="9.4" height="1" transform="translate(0.707) rotate(45)" fill="var(--button-color-text)"/>
									</svg>
								</a>
							</div>
						</div>
					</div> -->
					<!-- end -->
					<!-- slide nave -->

				</div>
			</SplideSlide>
		</Splide>
		<!-- slide nave -->
		<div class="nations-league-wrap">
			<div class="cust-row slider-dots">
				<div class="dots">
					<span v-if="bannersStore.homepage_banners.length > 0"
						v-for="(banner, index) in bannersStore.homepage_banners"
						:class="{ active: current_banner === banner.id }" :data-id="banner.id"
						@click="selectBanner(banner.id)"></span>
				</div>
			</div>
		</div>
		<!-- end -->
	</section>

	<section v-if="hasBanners" class="nations-league-wrap nations-league-banner" style="margin-top: 40px;">
		<div class="container-sm">
			<div class="cust-row">

				<template v-if="bannersStore.homepage_banners.length > 0"
					v-for="(banner, id) in bannersStore.homepage_banners" :key="id">
					<div class="cust-col-4" @click="selectBanner(banner.id)">
						<div class="league-card" :class="{ 'activeHeroBanner': current_banner === banner.id, 'low': bannersStore.homepage_banners.length > 2 }">
							<div class="card-image">
								<img :src="banner.media_url" alt="card-image" class="bg-img">

							</div>

								<div class="content">

									<div class="detail">
										<div :class="{ 'first-item-padding': banner.sort === 0 }">
											<div class="title">
												<p class="title-cont">{{ banner.title }}</p>
											</div>
											<div class="sub-title">
												<p>{{ banner.sub_title }}</p>
											</div>
										</div>
									</div>
								</div>

						</div>
					</div>
				</template>

			</div>



		</div>
	</section>

	<!-- mobile box section start -->
	<!-- <section class="nav-boxes-main" v-if="menuStore.getMobileLandingPageMenu.length > 0">
		<div class="container-full">
			<div class="nav-boxes-inner">
				<div class="nav-box-item" v-for="menu_item in menuStore.getMobileLandingPageMenu" @click="handleMenuBoxRedirect(menu_item.click_url)">
					<img class="nav-box-img" :src="menu_item.media_url">
					<p>{{ menu_item.name }}</p>
				</div>
			</div>
		</div>
	</section> -->
	<!-- mobile box section end -->
</template>
<style scoped>
.home-hero .slide-img img {
	mix-blend-mode: unset;
}


.slider-dots {
    position: relative;
}

.nations-league-wrap .cust-row .league-card .card-image .bg-img {
	mix-blend-mode: unset;
}

.slider-dots .dots span {
    display: block;
    width: 28px;
    height: 2px;
    background: var(--off-gray);
    border-radius: 10%;
    cursor: pointer;
}
.slider-dots .dots {
    width: max-content;
    margin: 0 auto;
    padding: 18px 0 0;
    display: grid;
    grid-auto-columns: minmax(28px, auto);
    grid-gap: 10px;
    grid-auto-flow: column;
}

.slider-dots .dots span.active {
    background-color: var(--primary);
}

.cust-row {
    display: grid;
    grid-auto-flow: column;
    scroll-behavior: smooth;
    grid-template-columns: repeat(auto-fit, minmax(30%, 1fr));
    grid-gap: 17px;
}
.home-hero {
	/* margin-top: 20px; */
    max-width: 1496px;
    margin-left: auto;
    margin-right: auto;
	border-radius: 25px;
	overflow: hidden;
    /* padding-left: 20px;
    padding-right: 20px; */
}

.home-hero :deep(.splide),
.home-hero :deep(.splide__track),
.home-hero :deep(.splide__list),
.home-hero :deep(.splide__slide) {
	height: 100%;
}
.active {
	background: var(--primary) !important;
}

.view-all-btn {
    background: transparent;
    color: var(--white);
    border: 2px solid rgba(255, 255, 255, 0.85);
    border-radius: 8px;
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.25s
	ease, box-shadow 0.25s
	ease, border-color 0.25s
	ease;
}

.home-hero .banner-content{
    top: 50% !important;
}

.home-hero .slide-img {
	position: unset;
	background: transparent;
	height: 100%;
}
.banner-btn a:hover svg rect {
  fill: var(--button-color-text-hover) !important;
}
.is-clickable {
	cursor: pointer;
}
.banner-btn {
	height: 46px;
	margin-top: 8px !important;
}

.league-card {
	cursor: pointer;
	margin-top: 0px;
	transition: margin-top 0.3s ease-in-out;
}

.home-hero .banner-content .sub-title {
    padding-top: 9px !important;
}

.nations-league-wrap .cust-row .league-card .card-image {
   width: 48%;
}

.nations-league-wrap .cust-row .league-card .content {
	width: 48%;
	padding: unset;
}

.nations-league-wrap .cust-row .league-card .content .detail {
	padding: unset;
}

[dir="rtl"] .nations-league-wrap .cust-row .league-card .card-image {
    left: auto;
    right: 0;
    border-radius: 0 5px 5px 0;
}

[dir="rtl"] .nations-league-wrap .cust-row .league-card .content {
    right: auto;
    left: 0;
}

.activeHeroBanner {
	margin-top: -20px;
}

@media screen and (min-width: 320px) and (max-width: 370px){
	.first-item-padding {
		padding-left: 25px;
	}
	html[dir="rtl"] .first-item-padding {
		padding-left: unset;
		padding-right: 25px;
	}
}

.cust-row {
	scrollbar-width: none;
}

.home-hero .banner-content {
	text-align: left;
	margin-left: 140px;
	top: calc(50% + 40px);
}

html[dir="rtl"] .home-hero .banner-content {
	text-align: right;
	margin-left: unset;
	margin-right: 140px;
}

.home-hero .banner-content h1 {
	max-width: 100%;
}

@media screen and (min-width: 320px) and (max-width: 767px) {
	.nations-league-wrap {
		padding: 0px !important;
		margin-top: -50px !important;
	}

	.banner-content-area {
		display: unset;
	}

	.home-hero .banner-content .banner-btn {
		margin-top: 16px !important;
	}

}

.home-hero {
	height: 600px;
	padding-top: 100px;
}

@media screen and (min-width: 1600px) and (max-width: 1799px) {
	.home-hero {
		height: 615px;
	}

	.home-hero .banner-content {
		margin-left: 80px;
	}

	html[dir="rtl"] .home-hero .banner-content {
		margin-left: unset;
		margin-right: 80px;
	}
}

@media screen and (min-width: 1360px) and (max-width: 1599px) {
	.home-hero {
		height: 558px;
	}

	.home-hero .banner-content {
		margin-left: 75px;
	}

	html[dir="rtl"] .home-hero .banner-content {
		margin-left: unset;
		margin-right: 75px;
	}
}

@media screen and (min-width: 992px) and (max-width: 1359px) {
	.home-hero {
		height: 573px;
	}

	.home-hero .banner-content {
		margin-left: 55px;
	}

	html[dir="rtl"] .home-hero .banner-content {
		margin-left: unset;
		margin-right: 55px;
	}
}

@media screen and (min-width: 768px) and (max-width: 991px) {
	.home-hero {
		height: 590px;
	}

	.home-hero .banner-content {
		margin-left: 60px;
	}

	html[dir="rtl"] .home-hero .banner-content {
		margin-left: unset;
		margin-right: 60px;
	}

	.banner-content h5 {
		padding: unset !important
	}
}

@media screen and (min-width: 300px) and (max-width: 767px) {

	.home-hero .banner-content {
		margin-left: 23px;
		top: 202px;
	}

	html[dir="rtl"] .home-hero .banner-content {
		margin-left: unset;
		margin-right: 23px;
	}

	.home-hero .banner-content h1 {
		line-height: unset;
	}

	.home-hero .banner-content .sub-title {
		padding-top: 5px !important;
	}
}

.h-100 {
	height: 100%;
}

.mw-60 {
	max-width: 60%;
}

.fw-400 {
	font-weight: 400;
}

.nav-boxes-main {
	margin: unset !important;
}

.nav-boxes-main .nav-box-item {
	padding: 10px;
}

.mt-50px {
	margin-top: 50px !important;
}

.nav-boxes-main .nav-box-item p {
	text-align: center;
}


.home-hero .slide-img img {
	border-radius: 25px;
	/* height: 500px; */
	height: 100%;
	max-width: 1496px;
	width: 100%;
	object-fit: cover;
}

.h-100 {
	border-radius: 25px;
}

.nations-league-wrap .cust-row .league-card .card-image {
    flex-basis: 50%;
    background: transparent;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 50%;
    border-radius: 5px 0 0 5px;
    overflow: hidden;
}

.nations-league-wrap .cust-row .league-card .content {
	width: 50%;
	background: linear-gradient(1166deg, grey -252%, var(--header-background-color) 130%);
}

.nations-league-wrap .cust-row .league-card .content .detail {
	padding-left: 5px;
}

@media screen and (min-width: 300px) and (max-width: 767px) {
    .home-hero {
        height: 280px;
		width: 95vw;
		padding-top: 70px;
		padding-left: 0px;
    	padding-right: 0px;
    }

	.nav-boxes-main {
		margin-top:20px !important;
		padding-bottom: 20px;
	}


}

.nations-league-wrap .cust-row .league-card {
	border: unset;
	border-radius: 25px;
}

.home-hero .banner-content h1 {
	font-size: 50px;
}


.nations-league-wrap {
    position: relative;
    padding: 0px;
    margin-top: -59px;
    z-index: 1;
    /* padding-top: 100px; */
    /* overflow: hidden; */
}

.container-sm {
    max-width: 1496px;
    margin-left: auto;
    margin-right: auto;
    /* padding-left: 20px;
    padding-right: 20px; */
}

.cust-row {
    display: grid;
    grid-auto-flow: column;
    scroll-behavior: smooth;
    grid-template-columns: repeat(auto-fit, minmax(30%, 1fr));
    grid-gap: 17px;
}

.nations-league-wrap .cust-row .cust-col-4 {
    width: 100%;
}

.nations-league-wrap .cust-row .league-card {
    border: unset;
    border-radius: 25px;
}

.nations-league-wrap .cust-row .league-card {
    display: flex;
    width: 100%;
    /* background: var(--primary); */
    position: relative;
    border: 1px solid var(--background-third-color);
    height: 0;
	padding-top: 30%;
    overflow: hidden;
}

.nations-league-wrap .cust-row .league-card .card-image {
    flex-basis: 50%;
    background: transparent;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 50%;
    border-radius: 5px 0 0 5px;
    overflow: hidden;
}

.nations-league-wrap .cust-row .league-card .card-image .bg-img  {
    mix-blend-mode: unset;
}
.nations-league-wrap .cust-row .league-card .card-image .bg-img {
    position: absolute;
    height: 100%;
    width: 100%;
    object-fit: cover;
    mix-blend-mode: luminosity;
}

.nations-league-wrap .cust-row .league-card .content {
    width: 50%;
    background: linear-gradient(1166deg, grey -252%, var(--dark-gray) 130%);
}
.nations-league-wrap .cust-row .league-card .content {
    width: 48%;
    padding: unset;
}
.nations-league-wrap .cust-row .league-card .content {
    flex-basis: 50%;
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 10px;
    color: var(--white);
}

.nations-league-wrap .cust-row .league-card .content .detail {
    padding-left: 5px;
}

.nations-league-wrap .cust-row .league-card .content .title-cont {
    font-size: 18px;
    line-height: 24px;
    font-weight: 400;
}

.nations-league-wrap .cust-row .league-card .content .sub-title {
    font-size: 14px;
    line-height: 20px;
    font-weight: 200;
    margin-top: 5px;
}

.low {
	padding-top: 40%;
}

@media screen and (max-width: 991px) {
    .nations-league-banner {
		display: none;
    }
}

</style>